import React, { useEffect, useRef, useState } from 'react'
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { DashboardAreaKey } from '../types'
import { Box } from '@mui/material'

const AREA_HEIGHT_OPENED = 120
const AREA_HEIGHT_CLOSED = 58
const NEGATIVE_MARGIN_TO_OPEN_AREA = 25
const INNER_AREA_HEIGHT_OPENED = 54

interface DroppableProps {
  index: number
  handleOnDrop: (oldPosition: number, newPosition: number, name: DashboardAreaKey) => void
}

/**
 * Droppable component for the dashboard editor
 */
const Droppable: React.FC<DroppableProps> = ({ index, handleOnDrop }) => {
  const ref = useRef(null)
  const [isDraggedOver, setIsDraggedOver] = useState(false)

  useEffect(() => {
    const element = ref.current

    if (!element) return

    return dropTargetForElements({
      element,
      onDragEnter: () => {
        setIsDraggedOver(true)
      },
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: args => {
        setIsDraggedOver(false)
        handleOnDrop(args.source.data.position as number, index, args.source.data.areaKey as DashboardAreaKey)
      },
      canDrop: args => {
        const canDrop = args.source.data.position !== index && (args.source.data.position as number) + 1 !== index
        return canDrop
      },
    })
  }, [handleOnDrop, index])

  return (
    <Box
      ref={ref}
      sx={theme => ({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: isDraggedOver ? AREA_HEIGHT_OPENED : AREA_HEIGHT_CLOSED,
        transition: 'all 0.2s',
        boxShadow: theme.shadows[0],
        position: 'relative',
        marginTop: `-${NEGATIVE_MARGIN_TO_OPEN_AREA}px`,
        marginBottom: `-${NEGATIVE_MARGIN_TO_OPEN_AREA}px`,
      })}
    >
      <Box
        sx={theme => ({
          width: '100%',
          transition: 'all 0.2s',
          height: isDraggedOver ? INNER_AREA_HEIGHT_OPENED : 0,
          borderRadius: `${theme.shape.borderRadius}px`,
          border: isDraggedOver ? `2px dashed ${theme.palette.primary.main}` : 'none',
          backgroundColor: isDraggedOver ? theme.palette.primary.light : 'none',
        })}
      />
    </Box>
  )
}

export default Droppable
